import { Subject, Subscription } from 'rxjs';
import { Model } from '@df/catalog/model';
import { Listing } from '@df/catalog-ui/listing/listing';
import { ListingSortAttributeEngine } from '@df/catalog-ui/listing/sort/listing-sort-attribute-engine';
import { Status } from '@df/core/status';
import { StandardSerializer } from '@df/util/fn/standard-serializer';

export interface IListingSortOption {
    code?: string;
    desc?: boolean;
    label: string;
    active?: boolean;
    engine?: string;
    default?: boolean;
}

export interface IListingSortEngine<T extends Model = Model> {
    sort(collection: T[], option: IListingSortOption, listing: Listing<T>, cb: (models: T[]) => void): void;
}

export class ListingSort<T extends Model = Model> {
    /**
     * Listing sort engines
     */
    static engines: { [id: string]: IListingSortEngine<any> } = {
        attribute: new ListingSortAttributeEngine()
    };

    /**
     * Subject for option change observations
     */
    protected subject = new Subject<IListingSortOption | undefined>();

    /**
     * Status model for async sort
     */
    status = new Status();

    /**
     * Sort options
     */
    protected options!: IListingSortOption[];

    /**
     * Current selection
     */
    selection?: IListingSortOption;

    constructor(
        protected listing: Listing<T>,
        options: IListingSortOption[]
    ) {
        this.setSortOptions(options);
    }

    /**
     * Returns currently selected selection label
     * @return {string}
     */
    get selectionLabel(): string {
        const selection = this.getSelection();
        return selection ? selection.label : '';
    }

    /**
     * Serializes its own data to string
     */
    getState(): string | undefined {
        if (this.selection && this.selection.code) {
            return <string>StandardSerializer.serialize((this.selection.desc ? '-' : '') + this.selection.code);
        }

        return undefined;
    }

    /**
     * Sets its own data from string
     */
    setState(source: string): void {
        this.selection = this.getOptionByOptionCode(source);
        this.cleanup();
    }

    getOptionByOptionCode(source: string): IListingSortOption | undefined {
        const options = this.options;
        const optionCode = StandardSerializer.unserialize(source);
        let option: IListingSortOption | undefined;

        if (optionCode) {
            const isDesc = optionCode[0] === '-';
            const code = isDesc ? optionCode.toString().substr(1) : optionCode;

            option = options.find(o => o.desc === isDesc && o.code === code);
        }
        return option;
    }

    getSelection(): IListingSortOption | undefined {
        return this.selection || this.options.find(option => option.default === true);
    }

    getOptions() {
        return this.options;
    }

    optionTrackFn(index: number, option: IListingSortOption) {
        return option.code;
    }

    /**
     * Triggers all registered callbacks
     */
    next() {
        this.subject.next(this.getSelection());
    }

    /**
     * Sets sort options
     *
     * @param {IListingSortOption[]} options
     */
    setSortOptions(options: IListingSortOption[]) {
        this.options = options;
        options.forEach(option => {
            if (option.desc === undefined) {
                option.desc = false;
            }
        });
        this.cleanup();
        this.next();
    }

    /**
     * Registers callback triggered when sort option changes
     */
    subscribe(...args): Subscription {
        return this.subject.subscribe(...args);
    }

    /**
     * Changes current sort option
     *
     * @param {IListingSortOption} sortOption
     */
    by(sortOption: undefined | IListingSortOption) {
        this.selection = sortOption || undefined;
        this.cleanup();
        this.next();
    }

    /**
     * Sorts collection by currently selected sort option
     * More then one sortOption is allowed
     *
     * @param  {Model[]} collection input collection
     * @return {Model}              sorted collection
     */
    applySort(collection: T[], cb: (models: T[]) => void): void {
        const selection = this.getSelection();

        if (selection) {
            const engineName = selection.engine || 'attribute';
            const engine = ListingSort.engines[engineName] as any as IListingSortEngine<T>;

            if (engine) {
                engine.sort(collection, selection, this.listing, (models: T[]) => {
                    // only apply if still the same option
                    if (selection === this.getSelection()) {
                        cb(models);
                    }
                });
                return;
            } else {
                console.warn('Missing sort engine for sort option: ', engineName);
            }
        }

        cb(collection);
    }

    protected cleanup() {
        if (this.selection && !this.options.find(option => option.code === this.selection!.code && option.desc === this.selection!.desc)) {
            this.selection = undefined;
        }

        this.options.forEach(option => (option.active = this.getSelection() === option));
    }
}
